/*
 * <fc-carousel>
 * Wraps the embla <carousel-component> rendered by snippets/featured-collection-carousel.liquid
 * (used by the multiple-collections and featured-collection sections). Touch + mouse drag is
 * handled natively by embla via data-draggable, identically at every screen size. This element
 * wires the prev/next arrows and the progress bar to embla's API.
 *
 * The progress fill follows the scroll in real time, but writes are coalesced to one per
 * animation frame (rAF), rounded to whole percents, and skipped when unchanged — so embla's
 * per-frame scroll events don't spam inline-style mutations.
 */
class FcCarousel extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('carousel-component');
    this.prevBtn = this.querySelector('[data-direction="prev"]');
    this.nextBtn = this.querySelector('[data-direction="next"]');
    this.progressEl = this.querySelector('[slider-grid-progress]');
    this.viewportEl = this.querySelector('[data-carousel-viewport]');
    this.containerEl = this.querySelector('[data-carousel-container]');
    this.isThumb = this.progressEl ? this.progressEl.hasAttribute('data-progress-thumb') : false;
    if (!this.carousel) return;

    this.lastProgress = -1;
    this.rafId = 0;
    this.requestUpdate = this.requestUpdate.bind(this);
    this.render = this.render.bind(this);
    this.onPrev = () => this.embla && this.embla.scrollPrev();
    this.onNext = () => this.embla && this.embla.scrollNext();
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.requestUpdate);

    this.waitForEmbla();
  }

  waitForEmbla(attempt = 0) {
    const embla = this.carousel && this.carousel.embla;
    if (embla) {
      this.embla = embla;
      embla.on('scroll', this.requestUpdate);
      embla.on('select', this.requestUpdate);
      embla.on('settle', this.requestUpdate);
      embla.on('reInit', this.requestUpdate);
      this.guardNestedDrag();
      this.render();
      return;
    }
    if (attempt < 60) setTimeout(() => this.waitForEmbla(attempt + 1), 50);
  }

  guardNestedDrag() {
    if (!this.carousel.hasAttribute('data-draggable')) return;
    const allowDrag = (api, evt) =>
      !(evt.target && evt.target.closest && evt.target.closest('.product-card__swiper-wrap'));
    this.carousel.reInit({ watchDrag: allowDrag });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected && this.carousel) this.carousel.reInit({ watchDrag: allowDrag });
      });
    }
  }

  requestUpdate() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.render();
    });
  }

  render() {
    if (!this.embla) return;
    let p = this.embla.scrollProgress();
    if (Number.isNaN(p)) p = 0;
    p = Math.max(0, Math.min(1, p));

    if (this.progressEl) {
      if (this.isThumb) {
        this.renderThumb(p);
      } else {
        const pct = Math.round(p * 100);
        if (pct !== this.lastProgress) {
          this.lastProgress = pct;
          this.progressEl.style.setProperty('--gsc-slider-grid-progress', pct + '%');
        }
      }
    }

    if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', !this.embla.canScrollPrev());
    if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', !this.embla.canScrollNext());
  }

  // Thumb: width = fraction of content in view, position = scroll progress across the track.
  renderThumb(p) {
    const track = this.progressEl.parentElement;
    const trackW = track ? track.clientWidth : 0;
    const viewW = this.viewportEl ? this.viewportEl.clientWidth : 0;
    const contentW = this.containerEl ? this.containerEl.scrollWidth : 0;
    if (!trackW || !contentW) return;

    let frac = viewW / contentW;
    frac = Math.max(0.08, Math.min(1, frac || 1));
    const thumbW = Math.round(frac * trackW);
    const offset = Math.round(p * (trackW - thumbW));
    this.progressEl.style.setProperty('--pb-thumb-size', thumbW + 'px');
    this.progressEl.style.setProperty('--pb-thumb-offset', offset + 'px');
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.requestUpdate);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
    if (this.embla) {
      this.embla.off('scroll', this.requestUpdate);
      this.embla.off('select', this.requestUpdate);
      this.embla.off('settle', this.requestUpdate);
      this.embla.off('reInit', this.requestUpdate);
    }
  }
}

if (!customElements.get('fc-carousel')) {
  customElements.define('fc-carousel', FcCarousel);
}
